import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { toast } from 'react-toastify';

const DEFAULT_EXTENSIONS = {
  images: ['png', 'gif', 'jpeg', 'jpg'],
  documents: ['doc', 'docx', 'txt'],
  sheets: ['xls', 'xlsx', 'csv'],
  zips: ['zip', 'rar', '7zip'],
  get all(): string[] {
    if (!(this as any)._all) {
      (this as any)._all = [...this.images, ...this.documents, ...this.sheets, ...this.zips];
    }

    return (this as any)._all;
  }
};

interface IProps {
  extensions?: string[];
}

export interface FileSelectorHandle {
  open: () => Promise<File>;
}

const hiddenStyle = { display: 'none' } as const;

const FileSelector = forwardRef<FileSelectorHandle, IProps>(({ extensions = DEFAULT_EXTENSIONS.all }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const resolveRef = useRef<((file: File) => void) | null>(null);

  const windowFocus = useCallback(() => {
    setTimeout(() => {
      if (!resolveRef.current) {
        return;
      }

      resolveRef.current(null);
    }, 500);
  }, []);

  useEffect(() => {
    window.addEventListener('focus', windowFocus);

    return () => {
      window.removeEventListener('focus', windowFocus);
    };
  }, [windowFocus]);

  const onFileSelected = useCallback(() => {
    if (!inputRef.current.files.length) {
      resolveRef.current(null);
      return;
    }

    const file = inputRef.current.files[0];
    inputRef.current.value = null;

    const regexp = new RegExp(`.(${extensions.join('|')})$`, 'gi');

    if (!regexp.test(file.name)) {
      toast.error(`Apenas: ${extensions.join(', ')}`);
      return;
    }

    resolveRef.current(file);
  }, [extensions]);

  useImperativeHandle(ref, () => ({
    open: async (): Promise<File> => {
      inputRef.current.click();
      return new Promise<File>(resolve => {
        resolveRef.current = resolve;
      });
    }
  }));

  return (
    <input
      type='file'
      style={hiddenStyle}
      ref={inputRef}
      onChange={onFileSelected}
      accept={`.${extensions.join(',.')}`}
    />
  );
});

FileSelector.displayName = 'FileSelector';

export default FileSelector;
